import { Trans, useLingui } from '@lingui/react/macro'

import { UserEmailBadge } from '$src/components/company-portal/UserEmailBadge'
import {
  DocumentsSection,
  type DocEntry,
} from '$src/components/company-portal/PropertyDocuments'
import { customerHooks, propertyHooks, visitHooks } from '$src/lib/trpcHooks'
import {
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core'
import { AppPage } from '@markku/components'
import type { VisitWithRelations } from '@markku/types'
import { sortBy } from '@markku/utils'
import { IconCalendarEvent, IconChevronRight } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

function PropertyDetail() {
  const { t } = useLingui()
  const { companyId, propertyId } = Route.useParams()
  const navigate = useNavigate()

  const { data: company } = customerHooks.useGetById(Number(companyId))
  const { data: property, isLoading: isLoadingProperty } =
    propertyHooks.useGetById(Number(propertyId))

  // Fetch last completed visit
  const { data: lastVisitData } = visitHooks.useList(
    {
      filters: {
        propertyId: { eq: Number(propertyId) },
        status: { eq: 'completed' },
      },
      orderBy: 'startTime',
      orderDirection: 'desc',
      pageSize: 1,
    },
    { enabled: !!propertyId },
  )

  // Fetch next scheduled visit
  const { data: nextVisitData } = visitHooks.useList(
    {
      filters: {
        propertyId: { eq: Number(propertyId) },
        status: { eq: 'scheduled' },
      },
      orderBy: 'startTime',
      orderDirection: 'asc',
      pageSize: 1,
    },
    { enabled: !!propertyId },
  )

  const isLoading = isLoadingProperty
  const backTarget = `/company-portal/${companyId}`
  const address = property?.address?.street || t`Property`
  const companyName = company?.name

  if (isLoading) {
    return (
      <AppPage
        title={address}
        subtitle={companyName}
        backTarget={backTarget}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </AppPage>
    )
  }

  if (property && property.customerId !== Number(companyId)) {
    return (
      <AppPage
        title={t`Property not found`}
        subtitle={companyName}
        backTarget={backTarget}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Text c="dimmed">
            <Trans>Property not found</Trans>
          </Text>
        </Center>
      </AppPage>
    )
  }

  const lastVisit = (lastVisitData?.data?.[0] ||
    null) as VisitWithRelations | null
  const nextVisit = (nextVisitData?.data?.[0] ||
    null) as VisitWithRelations | null

  const propertyAttachments: DocEntry[] = (property?.attachments || []).map(
    (att) => ({
      ...att,
      source: 'property' as const,
      date: att.createdAt,
    }),
  )

  const allDocuments = propertyAttachments.filter(
    (doc) => !doc.metadata?.stepId,
  )

  const sortNewest = (docs: DocEntry[]) =>
    sortBy(docs, [(d) => new Date(d.date).getTime(), 'desc'])

  const certificates = sortNewest(
    allDocuments.filter((doc) => doc.metadata?.type === 'certificate'),
  )
  const visitReports = sortNewest(
    allDocuments.filter((doc) => doc.metadata?.type === 'visit_report'),
  )
  const otherDocuments = sortNewest(
    allDocuments.filter(
      (doc) =>
        doc.metadata?.type !== 'certificate' &&
        doc.metadata?.type !== 'visit_report',
    ),
  )

  return (
    <AppPage
      title={address}
      subtitle={companyName}
      backTarget={backTarget}
      rightSection={<UserEmailBadge />}
    >
      <Stack gap="xl">
        <Group gap="sm" wrap="nowrap" align="stretch">
          {lastVisit && (
            <VisitCard
              visit={lastVisit}
              label={t`Last visit`}
              companyId={companyId}
            />
          )}

          {nextVisit && (
            <VisitCard
              visit={nextVisit}
              label={t`Next visit`}
              companyId={companyId}
            />
          )}

          <Button
            variant="light"
            leftSection={<IconCalendarEvent size={18} />}
            onClick={() =>
              navigate({
                to: `/company-portal/${companyId}/visits`,
                search: { property: propertyId },
              })
            }
            style={{ flex: 1, alignSelf: 'center' }}
          >
            <Trans>Kohteen käynnit</Trans>
          </Button>
        </Group>

        <DocumentsSection
          certificates={certificates}
          visitReports={visitReports}
          otherDocuments={otherDocuments}
        />
      </Stack>
    </AppPage>
  )
}

function VisitCard({
  visit,
  label,
  companyId,
}: {
  visit: VisitWithRelations
  label: string
  companyId: string
}) {
  const navigate = useNavigate()

  const taskList =
    visit.visitTasks?.map((vt) => vt.maintenanceTask?.name).filter(Boolean) ||
    []
  const tasksToShow = taskList.slice(0, 2)
  const remainingCount = taskList.length - tasksToShow.length

  const dateTimeStr = new Date(visit.startTime).toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <Card
      withBorder
      radius="md"
      p="sm"
      style={{ flex: 1, cursor: 'pointer', minHeight: '110px' }}
      onClick={() =>
        navigate({
          to: `/company-portal/${companyId}/visits/${visit.id}`,
        })
      }
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            {label}
          </Text>
          <Text size="sm" fw={600}>
            {dateTimeStr}
          </Text>
          {taskList.length > 0 && (
            <Group gap={4}>
              {tasksToShow.map((task, idx) => (
                <Badge key={idx} size="sm" variant="light" color="gray">
                  {task}
                </Badge>
              ))}
              {remainingCount > 0 && (
                <Badge size="sm" variant="light" color="gray">
                  +{remainingCount}
                </Badge>
              )}
            </Group>
          )}
        </Stack>
        <IconChevronRight size={18} style={{ flexShrink: 0, marginTop: 4 }} />
      </Group>
    </Card>
  )
}

export const Route = createFileRoute(
  '/_app/company-portal/$companyId/properties/$propertyId/',
)({
  component: PropertyDetail,
})
